"use client";

import { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { toast } from "react-toastify";
import { APIResponseEntity } from "../../../../interface/api-responce";
import { SaveHandleFabricationCodesReq, TBSHandleFabricationSummaryEntity } from "../../../../interface/tbs-tracker/tbs-store";
import { APIGetHandleFabricationCodes, APISaveHandleFabricationCodes } from "../../../../service/api-service";
import { toastOptObj } from "../../../../utils/constants";
import { SolutionCategoryEnum } from "../../../../utils/enum";
import { TBS_FABRICATION_CODE_PATTERN } from "../../../../utils/pattern";

interface FabricationCodeDialogProps {
    isOpen: boolean;
    onClose: () => void;
    /** called after the codes were saved (the store listing refreshes its counts) */
    onSaved: () => void;
    tbsId: string;
    tbsNumber: string;
}

// one handle location: the code already saved for it and what is typed now
interface FabricationRowEntity {
    locationId: string;
    orderNumber: string;
    projectName: string;
    solutionNo: string;
    category: string;
    csCode: string;
    floor: string;
    systemsArr: string[];
    specsArr: string[];
    savedCode: string;
    code: string;
}

export default function FabricationCodeDialog({
    isOpen,
    onClose,
    onSaved,
    tbsId,
    tbsNumber,
}: FabricationCodeDialogProps) {
    const [rows, setRows] = useState<FabricationRowEntity[]>([]);
    const [canEdit, setCanEdit] = useState<boolean>(true);
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

    const applySummary = (summary: TBSHandleFabricationSummaryEntity) => {
        setCanEdit(summary.canEdit);
        setRows(
            summary.handles.map((handle) => ({
                locationId: handle.locationId,
                orderNumber: handle.orderNumber,
                projectName: handle.projectName,
                solutionNo: handle.solutionNo,
                category: handle.category,
                csCode: handle.csCode,
                floor: handle.floor,
                systemsArr: handle.systemsArr || [],
                specsArr: handle.specsArr || [],
                savedCode: handle.fabricationCode,
                code: handle.fabricationCode,
            }))
        );
    };

    const fetchHandles = async () => {
        setIsFetching(true);
        try {
            const resp = await APIGetHandleFabricationCodes(tbsId);
            if (!resp || !resp.data || resp.status !== 200) {
                return;
            }
            const apiResp: APIResponseEntity = resp.data;
            if (apiResp.statusCode === 200 && apiResp.status && apiResp.data) {
                applySummary(apiResp.data as TBSHandleFabricationSummaryEntity);
            } else {
                toast.error(apiResp.message || apiResp.errorMsg, { ...toastOptObj });
            }
        } catch (error) {
            console.log({ error });
        } finally {
            setIsFetching(false);
        }
    };

    const getRowError = (row: FabricationRowEntity): string => {
        const code = row.code.trim();

        if (code === "") {
            // a saved code can be changed but not removed (a handle may already depend on it)
            return row.savedCode !== "" ? "Fabrication Code cannot be removed, enter the correct code" : "";
        }
        if (!TBS_FABRICATION_CODE_PATTERN.test(code)) {
            return "Use letters, numbers, space and - _ / . only (max 30 characters)";
        }
        return "";
    };

    // same lines and order as the System / Specs columns of the TBS store view page
    const renderSystem = (row: FabricationRowEntity) => {
        const [wall, flush, fireRated, direction, , dropSeal, handle, doorCloser] = row.systemsArr;
        return (
            <div className="small">
                {wall && <div>{wall}</div>}
                {flush && <div>{flush}</div>}
                {fireRated && <div>{fireRated}</div>}
                {direction && <div>{direction}</div>}
                {row.csCode && <div>Code: {row.csCode}</div>}
                {dropSeal && <div>{dropSeal}</div>}
                {handle && <div>{handle}</div>}
                {doorCloser && <div>{doorCloser}</div>}
                {row.systemsArr.length === 0 && !row.csCode && "-"}
            </div>
        );
    };

    const renderSpecs = (row: FabricationRowEntity) => {
        const [, , size, fireRating, frame, glassOut, glassIn] = row.specsArr;
        return (
            <div className="small">
                {row.floor && <div>{row.floor}</div>}
                {size && <div>{size}</div>}
                {fireRating && <div>{fireRating}</div>}
                {frame && <div>{frame}</div>}
                {glassOut && <div>{glassOut}</div>}
                {glassIn && <div>{glassIn}</div>}
                {row.specsArr.length === 0 && !row.floor && "-"}
            </div>
        );
    };

    const renderLocation = (row: FabricationRowEntity) => {
        const badgeClass =
            row.category === SolutionCategoryEnum.GLASS
                ? "badge-glass"
                : row.category === SolutionCategoryEnum.WALTZ
                    ? "badge-waltz"
                    : "badge-accessory-only";

        return (
            <div className="d-flex align-items-center">
                <span>{row.solutionNo || "-"}</span>
                {row.category && <span className={`badge badge-secondary ${badgeClass} mx-2 px-3`}>{row.category}</span>}
            </div>
        );
    };

    const renderCode = (row: FabricationRowEntity) => {
        const error = getRowError(row);
        return (
            <>
                <InputText
                    className="form-control"
                    maxLength={30}
                    value={row.code}
                    disabled={!canEdit}
                    placeholder="Enter code"
                    onChange={(e) =>
                        setRows((prev) =>
                            prev.map((item) =>
                                item.locationId === row.locationId ? { ...item, code: e.target.value } : item
                            )
                        )
                    }
                />
                {isSubmitted && error && (
                    <small className="text-danger font-weight-500 d-block p-1">{error}</small>
                )}
            </>
        );
    };

    // the handle locations grouped by order, like the locations of the TBS store view page
    const orderGroups = rows.reduce<{ orderNumber: string; projectName: string; rows: FabricationRowEntity[] }[]>((groups, row) => {
        const group = groups.find((item) => item.orderNumber === row.orderNumber);
        if (group) {
            group.rows.push(row);
        } else {
            groups.push({ orderNumber: row.orderNumber, projectName: row.projectName, rows: [row] });
        }
        return groups;
    }, []);

    // only the handles whose code was entered or changed are sent
    const changedRows = rows.filter((row) => row.code.trim() !== "" && row.code.trim() !== row.savedCode);
    const codedHandles = rows.filter((row) => row.savedCode !== "").length;
    const hasError = rows.some((row) => getRowError(row) !== "");

    const handleSave = async () => {
        setIsSubmitted(true);

        if (hasError) {
            return;
        }
        if (changedRows.length === 0) {
            if (codedHandles > 0) {
                onClose();
                return;
            }
            toast.error("Please enter at least one Fabrication Code.", { ...toastOptObj });
            return;
        }

        setIsSaving(true);
        try {
            const reqBody: SaveHandleFabricationCodesReq = {
                tbsId,
                codes: changedRows.map((row) => ({ locationId: row.locationId, fabricationCode: row.code.trim() })),
            };
            const resp = await APISaveHandleFabricationCodes(reqBody);
            if (!resp || !resp.data || resp.status !== 200) {
                return;
            }
            const apiResp: APIResponseEntity = resp.data;
            if (apiResp.statusCode === 200 && apiResp.status) {
                toast.success(apiResp.message, { ...toastOptObj });
                onSaved();
                onClose();
            } else {
                toast.error(apiResp.message || apiResp.errorMsg, { ...toastOptObj });
            }
        } catch (error) {
            console.log({ error });
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        setIsSubmitted(false);
        if (isOpen && tbsId) {
            setRows([]);
            fetchHandles();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, tbsId]);

    return (
        <Dialog
            visible={isOpen}
            onHide={onClose}
            closable={false}
            draggable={false}
            resizable={false}
            showHeader={false}
            style={{ width: "1100px", maxWidth: "95vw" }}
            contentStyle={{ padding: "1rem" }}
        >
            <div className="px-2">
                <h4 className="card-title fw-bold text-center mb-3">
                    Capture Fabrication Code
                </h4>

                <div className="mb-3">
                    <div className="mb-2">
                        <strong>TBS Number:</strong> {tbsNumber}
                    </div>
                    <div>
                        <strong>Handles with Fabrication Code:</strong> {codedHandles} of {rows.length}
                    </div>
                    <small className="text-muted d-block mt-1">
                        Handles can be issued only after a Fabrication Code is captured for all of them.
                    </small>
                </div>

                {isFetching && <p className="text-muted mb-3">Loading handles...</p>}

                {!isFetching && rows.length === 0 && (
                    <p className="text-muted mb-3">No handle is assigned to this TBS.</p>
                )}

                {rows.length > 0 && (
                    <div style={{ maxHeight: "55vh", overflowY: "auto" }} className="mb-3">
                        {orderGroups.map((group) => (
                            <div className="accordion mb-3" key={group.orderNumber}>
                                <div className="accordion-item rounded-3 shadow-sm">
                                    <div className="px-3 py-3 fw-bold">
                                        {group.orderNumber}
                                        {group.projectName ? ` - ${group.projectName}` : ""}
                                    </div>
                                    <div className="px-3 pb-3 pt-0">
                                        <DataTable
                                            value={group.rows}
                                            dataKey="locationId"
                                            responsiveLayout="scroll"
                                            showGridlines
                                            className="p-datatable-sm"
                                        >
                                            <Column header="Location No" body={renderLocation} style={{ width: "150px" }} />
                                            <Column header="System" body={renderSystem} style={{ minWidth: "260px" }} />
                                            <Column header="Specs" body={renderSpecs} style={{ minWidth: "220px" }} />
                                            <Column header="Fabrication Code" body={renderCode} style={{ minWidth: "220px" }} />
                                        </DataTable>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="d-flex justify-content-end">
                    <button
                        type="button"
                        className="btn btn-primary btn-md font-weight-medium me-2"
                        onClick={onClose}
                    >
                        Close
                    </button>

                    {canEdit && rows.length > 0 && (
                        <button
                            type="button"
                            className="btn btn-success btn-md font-weight-medium"
                            onClick={handleSave}
                            disabled={isSaving || isFetching}
                        >
                            Save
                        </button>
                    )}
                </div>
            </div>
        </Dialog>
    );
}
